import React, { useRef } from 'react';
import { dotVarFor, formatPrice, type LaCarteWine } from './laCarteModel';
import { WINE_COLOR_LABEL } from '../../../utils/wineColor';

/**
 * Detail surface: spring bottom sheet below 1200px, sticky right side-pane
 * at ≥1200px (the CSS decides — same markup). Two content modes:
 *   wine  — producer / serif name / meta, sommelier note, format+price rows,
 *           add-to-picks button
 *   picks — the guest's shortlist with a share action
 */
export type SheetMode =
  | { kind: 'empty' }
  | { kind: 'wine'; wine: LaCarteWine }
  | { kind: 'picks'; picks: LaCarteWine[] };

interface Props {
  mode: SheetMode;
  /** Whether the mobile sheet is open (ignored by the ≥1200 pane). */
  open: boolean;
  pickedIds: ReadonlySet<string>;
  onTogglePick: (id: string) => void;
  onSharePicks: () => void;
  onClose: () => void;
}

const DetailSheet: React.FC<Props> = ({ mode, open, pickedIds, onTogglePick, onSharePicks, onClose }) => {
  const sheetRef = useRef<HTMLElement>(null);
  const startY = useRef<number | null>(null);

  // Swipe-to-dismiss — bottom sheet only. The ≥1200 pane has no scrim and
  // position:sticky, so the transform is inert there.
  const isPane = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1200px)').matches;
  const onTouchStart = (e: React.TouchEvent) => {
    if (!isPane() && (sheetRef.current?.scrollTop ?? 0) <= 0) startY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null || !sheetRef.current) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) {
      sheetRef.current.style.transform = `translateY(${dy}px)`;
      sheetRef.current.style.transition = 'none';
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startY.current == null || !sheetRef.current) return;
    const dy = e.changedTouches[0].clientY - startY.current;
    sheetRef.current.style.transform = '';
    sheetRef.current.style.transition = '';
    if (dy > 90) onClose();
    startY.current = null;
  };

  return (
    <>
      <div
        className={`lc-scrim ${open ? 'lc-open' : ''}`.trim()}
        onClick={onClose}
        aria-hidden="true"
      />
      <section
        ref={sheetRef}
        className={`lc-sheet ${open ? 'lc-open' : ''}`.trim()}
        role="dialog"
        aria-label={mode.kind === 'picks' ? 'My picks' : 'Wine detail'}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="lc-grabber" />
        {mode.kind === 'empty' && <p className="lc-empty">Select a wine from the list</p>}

        {mode.kind === 'wine' && (() => {
          const w = mode.wine;
          const picked = pickedIds.has(w.id);
          // Full hierarchy in the sheet (the card carries the compact form).
          const metaBits = [
            w.color ? WINE_COLOR_LABEL[w.color] : null,
            w.regionParts.length > 0 ? w.regionParts.join(' · ') : null,
          ].filter(Boolean).join(' · ');
          const facts: Array<{ label: string; value: string }> = [];
          if (w.grapes.length > 0) facts.push({ label: w.grapes.length > 1 ? 'Grapes' : 'Grape', value: w.grapes.join(', ') });
          if (w.designation) facts.push({ label: 'Appellation', value: w.designation });
          if (w.classification) facts.push({ label: 'Classification', value: w.classification });
          return (
            <>
              {w.producer && <div className="lc-producer">{w.producer}</div>}
              <h3 className="lc-name lc-serif">
                {w.name}{' '}
                <span style={{ color: 'var(--muted)', fontStyle: 'normal' }}>{w.vintage ?? 'NV'}</span>
              </h3>
              <div className="lc-meta">
                <span className="lc-dot" style={{ background: dotVarFor(w.color) }} />
                {metaBits}
              </div>
              {facts.length > 0 && (
                <div className="lc-facts">
                  {facts.map(f => (
                    <div className="lc-fact" key={f.label}>
                      <b>{f.label}</b>
                      <span>{f.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {w.note && <p className="lc-note lc-serif">{w.note}</p>}
              {w.stockBand === 'LOW_STOCK' && (
                <p className="lc-stockline lc-stock--low">Only a few bottles remain</p>
              )}
              {w.stockBand === 'SOLD_OUT' && (
                <p className="lc-stockline lc-stock--out">Currently sold out — ask your sommelier</p>
              )}
              <div className="lc-formats">
                {w.formats.map((f, i) => (
                  <div className="lc-format" key={`${f.label}-${i}`}>
                    <span className="lc-lbl"><b>{f.label}</b>{f.sub && <span>{f.sub}</span>}</span>
                    <span className="lc-p">{formatPrice(f.price, w.currency)}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={`lc-pickbtn ${picked ? 'lc-in' : ''}`.trim()}
                onClick={() => onTogglePick(w.id)}
              >
                {picked ? 'Remove from my picks' : 'Add to my picks'}
              </button>
            </>
          );
        })()}

        {mode.kind === 'picks' && (
          <>
            <div className="lc-producer">For the table</div>
            <h3 className="lc-name lc-serif">My picks</h3>
            <div className="lc-formats">
              {mode.picks.map(w => (
                <div className="lc-format" key={w.id}>
                  <span className="lc-lbl">
                    <b>{w.producer ?? w.name}</b>
                    <span>{w.producer ? `${w.name} ${w.vintage ?? 'NV'}` : w.vintage ?? 'NV'}</span>
                  </span>
                  <span className="lc-p">{formatPrice(w.minPrice, w.currency)}</span>
                </div>
              ))}
              {mode.picks.length === 0 && (
                <div className="lc-format"><span className="lc-lbl">Nothing picked yet</span></div>
              )}
            </div>
            {mode.picks.length > 0 && (
              <button type="button" className="lc-pickbtn" onClick={onSharePicks}>
                Share with the sommelier
              </button>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default DetailSheet;
